import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import _ from "lodash";

interface IState {
  modals: any;
  orderHistory: any;
  orderHistories: any;
  filter: any;
  sort: any;
  // リスト/カードの表示切替。検索条件ではなく表示設定なので filter の外に置く。
  // filter の中に入れていると、filter を初期化するレデューサー（reset /
  // resetFilter）が view まで "" に戻し、一覧の描画条件から外れて表示が消える。
  view: string;
  // 検索（1ページ目の取得）が実行中か。
  // global.isLoading はアプリ全体で共有されており、BaseViewModel.withLoading を
  // 通る他の通信（バーコード読取・納品トグル等）が無条件に解除してしまうため、
  // この画面のボタン制御とページ送り抑止には使えない。
  isSearching: boolean;
}

const initialState: IState = {
  orderHistories: {
    data: [],
    currentPage: null,
    total: 0,
    lastPage: null,
    itemPerPage: 10,
    isStopLoading: false,
  },
  orderHistory: null,
  filter: {
    clinicName: null,
    orderCode: null,
    patientName: null,
    keyword: "",
    classes: [],
    labStatus: [], // delivered, not_delivery
    duplicateTask: [], // 1, 0
    instructionStatus: [],
    startDate: null,
    endDate: null,
  },
  // po_view_mode が未設定（null）のユーザーでも一覧が描画されるよう既定を持たせる。
  // "" のままだと list.tsx の list / image どちらの分岐にも入らず、
  // データを取得しても何も表示されない。
  view: "list", // list, image
  isSearching: false,
  sort: {
    sort_by: "",
    sort_type: "",
  },
  modals: {
    showScan: false,
    showTrialConfirm: false,
    showConfirmOrderDetail: false,
    showImage: false,
    showConfirmRemoveImage: false,
    // showInstructionOrderScan: false,
    // showOverdateConfirmScan: false,
    // showOverdateFinishScan: false,
  },
};

// filter を初期状態に戻す。スプレッドだけだと classes などの配列が
// initialState と同じ参照になるため、配列は新しく作り直す。
const createInitialFilter = () => ({
  ...initialState.filter,
  classes: [],
  labStatus: [],
  duplicateTask: [],
  instructionStatus: [],
});

export const orderHistorySlice = createSlice({
  name: "orderHistory",
  initialState,
  reducers: {
    reset(state) {
      state.orderHistories = { ...initialState.orderHistories, data: [] };
      state.orderHistory = null;
      state.modals = {
        ...initialState.modals,
      };
      state.filter = createInitialFilter();
      // view を filter の外に出したぶん、ここで明示的に戻さないと
      // 同一タブで別ユーザーに切り替わったとき表示設定を引き継いでしまう。
      state.view = initialState.view;
      state.isSearching = false;
    },

    resetOrderHistory(state) {
      state.orderHistory = null;
    },

    resetData(state) {
      // data だけでなくページング状態も初期化する。
      // currentPage / isStopLoading を残すと、一覧を空にした直後の
      // スクロールで「古いページ番号 + 空の条件」の問い合わせが飛びうる。
      // data は新しい配列を渡す（スプレッドだけだと initialState の配列と
      // 参照を共有してしまう）。
      state.orderHistories = { ...initialState.orderHistories, data: [] };
    },

    updateSort(state, action) {
      state.sort = action.payload;
    },

    resetFilter(state) {
      state.filter = createInitialFilter();
    },

    setView(state, action) {
      state.view = action.payload;
    },

    setSearching(state, action) {
      state.isSearching = action.payload;
    },

    fetchOrderHistories(state, action) {
      let orderHistories = state.orderHistories;
      let data = action.payload;
      if (data.current_page > orderHistories.currentPage) {
        orderHistories.data = [...orderHistories.data, ...data.data];
      } else {
        orderHistories.data = [...data.data];
      }

      orderHistories.currentPage = data.current_page;
      orderHistories.itemPerPage = data.per_page;
      orderHistories.total = data.total;
      orderHistories.lastPage = data.last_page;
      
      if (orderHistories.currentPage === data.last_page || data.data.length < data.per_page) {
        orderHistories.isStopLoading = true;
      } else {
        orderHistories.isStopLoading = false;
      }

      state.orderHistories = orderHistories;
    },

    /*
      @action: {index, data}
    */
    updateOrderHistories(state, action) {
      let { index, data } = action.payload;
      let orderHistories = state.orderHistories;
      let orderHistory = orderHistories.data[index];
      orderHistory = { ...orderHistory, ...data };
      orderHistories.data[index] = { ...orderHistory };
      state.orderHistories = orderHistories;
    },

    fetchOrderHistory(state, action) {
      state.orderHistory = action.payload;
    },

    updateOrderHistory(state, action) {
      const { key, val } = action.payload;
      let orderHistory = { ...state.orderHistory };
      orderHistory[key] = val;
      state.orderHistory = orderHistory;
    },

    setFilter(state, action) {
      let filter = { ...state.filter };
      let { key, value } = action.payload;
      filter[key] = value;
      state.filter = filter;
    },

    showModal(state, action) {
      let data = action.payload;
      let modals = state.modals;
      const { key, val } = data;

      // reset all modal
      if (val === true) {
        for (const property in modals) {
          modals[property] = false;
        }
      }
      modals[key] = val;
      state.modals = modals;
    },
  },
  extraReducers: (builder) => {},
});

export default orderHistorySlice;
export const orderHistoryReducer = orderHistorySlice.reducer;
export const orderHistorySliceAction = orderHistorySlice.actions;
